import React, { useMemo, useState } from 'react';

import {
  ArrayInput,
  AutocompleteInput,
  minLength,
  minValue,
  NumberInput,
  RadioButtonGroupInput,
  RaRecord,
  required,
  SimpleFormIterator,
  SourceContextProvider,
  useNotify,
  useSourceContext,
} from 'react-admin';

import {
  Box,
  Radio,
  Typography,
  Card,
  CardContent,
  CardHeader,
  RadioGroup,
  FormLabel,
  Divider,
} from '@mui/material';

import {
  useFormContext, useWatch,
} from 'react-hook-form';
import { get, isEmpty, isNumber } from 'lodash';

import currencies from '../../../constants/currencies';

import RiskAdjustment from '../../../customFields/Charts/RiskAdjustment';
import useThresholdVariant from '../../../hooks/useThresholdVariant';

const Body = ({
  displayText,
  onlyRiskBased = false,
  isDeviation,
}: {
  displayText: string | undefined;
  onlyRiskBased?: boolean;
  isDeviation: boolean;
}) => {
  const { getSource } = useSourceContext();
  const { setValue, getValues } = useFormContext();

  const isGlobal = !!getValues(getSource('global'));

  const [selectedValue, setSelectedValue] = useState(isGlobal ? 'global' : 'riskBased');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
    if (event.target.value === 'global') { setValue(getSource('riskBased'), undefined); }
    if (event.target.value === 'riskBased') { setValue(getSource('global'), ''); }
  };

  const thresholdsValidation = (
    value: string,
    allValues: any,
  ) => {
    const globalValue = get(allValues, getSource('global')) as number;
    const riskBasedValue = get(allValues, getSource('riskBased')) as {
      initial: number;
      slider: number;
      slope: number;
    };

    const isGlobalOrRiskBasedDefined = isNumber(globalValue) || isNumber(riskBasedValue?.initial);

    if (!isGlobalOrRiskBasedDefined) {
      return '"Global" or "Riskbased" must be defined';
    }
    return undefined;
  };

  const label = getValues(getSource('currency')) ? `Thresholds for ${getValues(getSource('currency'))}` : 'Thresholds';

  return (
    <Box marginY={4}>
      <FormLabel>{label}</FormLabel>
      <RadioGroup>
        {!onlyRiskBased && (
          <Box marginY={2}>
            <Radio
              checked={selectedValue === 'global'}
              onChange={handleChange}
              value="global"
            />
            Global:
            {' '}
            <NumberInput
              label={displayText || getValues(getSource('currency'))}
              source="global"
              size="small"
              helperText={false}
              validate={isDeviation ? [
                thresholdsValidation,
                minValue(-100),
              ] : [thresholdsValidation, minValue(0)]}
              disabled={selectedValue !== 'global'}
            />
          </Box>
        )}
        <Box marginY={2} display="flex" alignItems="center">
          <Radio
            checked={selectedValue === 'riskBased'}
            onChange={handleChange}
            value="riskBased"
          />
          <RiskAdjustment
            validate={[thresholdsValidation]}
            displayText={displayText || getValues(getSource('currency'))}
            source="riskBased"
            isDeviation={isDeviation}
            disabled={selectedValue !== 'riskBased'}
          />
        </Box>
      </RadioGroup>
    </Box>
  );
};

const ThresholdInput = ({
  displayText,
  overrideCurrencies,
  allowCommon = false,
  source,
  onlyRiskBased = false,
  title,
  subheader,
  optional = false,
}: {
  displayText?: string;
  overrideCurrencies?: string[];
  allowCommon?: boolean;
  source: string;
  onlyRiskBased?: boolean;
  title: string;
  subheader: string;
  optional?: boolean;
}) => {
  const notify = useNotify();
  const thresholds = useWatch({ name: `ruleParameters.${source}.thresholds` });
  const { thresholdVariant, onThresholdVariantChange } = useThresholdVariant({ source });

  const currenciesInUse = useMemo(
    () => thresholds?.map((threshold: RaRecord) => threshold.currency),
    [thresholds],
  );

  const onChange = (currency: string) => {
    if (currenciesInUse.includes(currency)) {
      notify(`Duplicated currency: ${currency}`, { type: 'error' });
    }
  };

  const commonThresholdSourceContext = useMemo(() => ({
    getSource: (input: string) => `ruleParameters.${source}.commonThreshold.${input}`,
    getLabel: (input: string) => input,
  }), [source]);

  return (
    <Box width="100%">
      <Card variant="outlined">
        <CardHeader title={title} subheader={subheader} />
        <Divider />
        <CardContent>
          {allowCommon && (
            <Box display="flex" flexDirection="column">
              <Typography variant="label">Threshold variant</Typography>
              <RadioButtonGroupInput
                label={false}
                source={`ruleParameters.${source}.thresholdVariant`}
                defaultValue="currency"
                choices={[
                  { id: 'currency', name: 'Currency' },
                  { id: 'common', name: 'Common' },
                ]}
                onChange={onThresholdVariantChange}
                size="small"
                helperText={false}
              />
            </Box>
          )}
          {thresholdVariant === 'currency' && (
            <Box>
              <Typography variant="label">Add currency specific thresholds</Typography>
              <ArrayInput validate={optional ? [] : [required(), minLength(1)]} source={`ruleParameters.${source}.thresholds`} label={false}>
                <SimpleFormIterator
                  inline
                  disableReordering
                  fullWidth
                  sx={{
                    '& .RaSimpleFormIterator-line': {
                      borderBottom: 'none',
                      paddingY: 2,
                      '& .RaSimpleFormIterator-inline': {
                        rowGap: '1em',
                      },
                    },
                  }}
                >
                  <Box display="flex" flexDirection="column">
                    <AutocompleteInput
                      source="currency"
                      choices={
                        currencies
                          .filter(
                            (curr) => isEmpty(overrideCurrencies)
                            || overrideCurrencies?.includes(curr),
                          )
                          .map((curr) => ({ id: curr, name: curr }))
                      }
                      helperText={false}
                      onChange={onChange}
                      validate={[required()]}
                    />
                    <Body
                      displayText={displayText}
                      onlyRiskBased={onlyRiskBased}
                      isDeviation={source === 'form'}
                    />
                  </Box>
                </SimpleFormIterator>
              </ArrayInput>
            </Box>
          )}
          {thresholdVariant === 'common' && (
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="label">Add common threshold</Typography>
              <SourceContextProvider value={commonThresholdSourceContext}>
                <Body
                  displayText={displayText}
                  onlyRiskBased={onlyRiskBased}
                  isDeviation={source === 'form'}
                />
              </SourceContextProvider>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export const DefaultThresholdInput = ({
  displayText,
  overrideCurrencies,
  allowCommon,
  onlyRiskBased,
  optional,
}: {
  displayText?: string;
  overrideCurrencies?: string[];
  allowCommon?: boolean;
  onlyRiskBased?: boolean;
  optional?: boolean;
}) => (
  <ThresholdInput
    source="default"
    title="Default Threshold"
    subheader="Configure default thresholds to be used within the rule for all viable entities. It can either be an global value that will be applied independent of risk assessment or it can be risk based thresholds."
    displayText={displayText}
    overrideCurrencies={overrideCurrencies}
    allowCommon={allowCommon}
    onlyRiskBased={onlyRiskBased}
    optional={optional}
  />
);

export const KycFormThresholdInput = ({
  displayText,
  overrideCurrencies,
  allowCommon,
  onlyRiskBased,
  optional,
}: {
  displayText?: string;
  overrideCurrencies?: string[];
  allowCommon?: boolean;
  onlyRiskBased?: boolean;
  title?: string;
  subheader?: string;
  optional?: boolean;
}) => (
  <ThresholdInput
    source="form"
    title="Know Your Customer (KYC) application form threshold"
    subheader="KYC form thresholds apply on entities with completed KYC forms. A global value may be assigned to all entities, unaffected by their individual risk scores. Alternatively, choosing a risk-based threshold means that acceptable answers can vary, aligned with each entity risk score. For either option, every input is a predetermined percentage of deviation from the initial responses received in the KYC form. These thresholds can be tailored for each currency or a common threshold can be established for all currencies."
    displayText={displayText}
    overrideCurrencies={overrideCurrencies}
    allowCommon={allowCommon}
    onlyRiskBased={onlyRiskBased}
    optional={optional}
  />
);

export default ThresholdInput;
